// Campaign attribution: which blog post / ad / email produced this assessment.
//
// Read from the widget's OWN url. When the widget is embedded in an iframe the
// utm_* params sit on the PARENT page, so the host page must forward them onto the
// iframe src — the same forwarding the embed already does for `?a=` and `?mode=`
// (see web/README.md). Direct links to the widget work without any forwarding.
//
// Captured once and kept in localStorage, because the params are on the landing
// URL but `start` doesn't fire until the intake form is submitted — a refresh or a
// resumed draft in between would otherwise lose them.

import { hashParams } from './persistence.js';
import type { Attribution } from './types.js';

const STORAGE_KEY = 'tbo_attribution';

/** The only keys we accept. Anything else in the URL is ignored. */
const FIELDS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'] as const;

/** Values land in a CRM and a form post, so cap them and drop control characters. */
const MAX_LEN = 200;

/** Printable characters only — no control codes, no DEL. */
function isPrintable(ch: string): boolean {
  const code = ch.codePointAt(0) ?? 0;
  return code >= 32 && code !== 127;
}

function clean(value: string | null): string | undefined {
  if (!value) return undefined;
  const stripped = Array.from(value).filter(isPrintable).join('').trim();
  return stripped ? stripped.slice(0, MAX_LEN) : undefined;
}

function fromUrl(): Attribution {
  const found: Attribution = {};
  try {
    const url = new URL(window.location.href);
    const hash = hashParams(url);
    for (const key of FIELDS) {
      const value = clean(url.searchParams.get(key) ?? hash.get(key));
      if (value) found[key] = value;
    }
    // Only useful on the first hit; a same-site referrer just names our own page.
    const ref = clean(document.referrer);
    if (ref && !ref.startsWith(url.origin)) found.referrer = ref;
  } catch {
    /* malformed URL — attribution is optional, never block the flow */
  }
  return found;
}

function read(): Attribution | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Attribution) : null;
  } catch {
    return null;
  }
}

/** Capture on load if the URL carries anything; otherwise keep what we already had.
 *  First touch wins — a mid-flow reload without params must not blank the source. */
export function captureAttribution(): void {
  const found = fromUrl();
  if (Object.keys(found).length === 0) return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(found));
  } catch {
    /* private mode — attribution is best-effort */
  }
}

/** What to send with `start`. Undefined when nothing was ever captured. */
export function readAttribution(): Attribution | undefined {
  const stored = read();
  if (stored && Object.keys(stored).length > 0) return stored;
  const found = fromUrl();
  return Object.keys(found).length > 0 ? found : undefined;
}

export function clearAttribution(): void {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}
